import {HStack, Icon} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {KeyboardEvent} from 'react';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {translateWithFallback as t} from '../../utils/translateWithFallback';

interface StarRatingInputProps extends TestIdProps {
  value: number | null;
  onChange?: (value: number) => void;
  isReadOnly?: boolean;
}

const STAR_VALUES = [1, 2, 3, 4, 5];

export function StarRatingInput(props: StarRatingInputProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = STAR_VALUES.length;
    const nextIndex = match(event.key)
      .with('ArrowRight', 'ArrowDown', () => (index + 1) % count)
      .with('ArrowLeft', 'ArrowUp', () => (index + count - 1) % count)
      .with('Home', () => 0)
      .with('End', () => count - 1)
      .otherwise(() => null);

    if (isNil(nextIndex) || props.isReadOnly) {
      return;
    }

    event.preventDefault();
    const nextValue = STAR_VALUES.at(nextIndex);
    if (isNil(nextValue)) {
      return;
    }
    event.currentTarget.parentElement?.querySelectorAll('button').item(nextIndex)?.focus();
    props.onChange?.(nextValue);
  };

  return (
    <HStack role="radiogroup" aria-label={t('rating.stars.label', 'Rating')} spacing={1}>
      {STAR_VALUES.map((value, index) => (
        <StarButton
          key={value}
          type="button"
          role="radio"
          aria-label={t('rating.stars.star', '{count} stars', {count: value})}
          aria-checked={props.value === value}
          tabIndex={(props.value ?? 1) === value ? 0 : -1}
          disabled={props.isReadOnly}
          onClick={() => props.onChange?.(value)}
          onKeyDown={(event) => onKeyDown(event, index)}
          data-testid={suffixTestId(`star-${value}`, props)}
        >
          <Icon
            value="iconStar"
            size={7}
            color={value <= (props.value ?? 0) ? 'severity.warningBase' : 'text.tertiary'}
          />
        </StarButton>
      ))}
    </HStack>
  );
}

const StarButton = styled.button`
  border: 0;
  padding: ${({theme}) => theme.getSize(1)};
  background: ${({theme}) => theme.colors.general.transparent};
  cursor: pointer;
  user-select: none;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: thin solid ${({theme}) => theme.colors.accent.base};
    border-radius: ${({theme}) => theme.radii.small};
  }
`;
